import { useState, useRef, useEffect, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import { styled } from "@mui/material/styles";
import { Formik, Form } from "formik";
import validations from "./schemas/validations";
import { flow as flowFields } from "./schemas/flow";
import initialValues from "./schemas/initialValues";
import Box from "@mui/material/Box";
import SoftButton from "components/SoftButton";
import { updateCurrentFlowProperties, createNewFlowProperties, setSavingFlowPropertiesFlagReset } from "redux/actions/flowDesigner/flowsActionCreator";
import { openDiscardDialog } from "redux/actions/flowDesigner/commonActionCreator";
import { toast } from "react-toastify";
import DiscardChangesDialog from "layouts/Flow Designer/components/DiscardChangesDialog";
import { exportToJson,isValidJsonString } from "helpers/common";
import { NODE_TYPES, EDGE_TYPES } from "constants/flowDesigner";
import { CREDENTIAL_REQUIRED, CREDENTIAL_TYPE, ORIGINAL_CREDENTIAL_TYPE } from "layouts/Flow Designer/components/ComponentActionsGenerator/components/CredentialListItem";
import { useIntegrations } from "hooks/useIntegrations";
import SoftBox from "components/SoftBox";
import { tryCatchWrapper } from "helpers/common";
import { insertIntegrations } from "api/Integrations";
import { addIntegration } from "redux/actions/portal/integrationActionCreator";
import { adjustTriggerCredentials } from "layouts/Flow Designer/helpers/common";
import { clearToolboxSelectedMenuItem, closeToolboxDetails } from "redux/actions/flowDesigner/settingsActionCreator";
import { setContext } from "redux/actions/flowDesigner/editorContextActionCreator";
import { getEnv } from "helpers/environment";
import { FlowFormContext } from "./context";

const ButtonsWrapper = styled(Box)(({ theme }) => ({
   display: "flex",
   flexShrink: 0,
   justifyContent: "flex-end",
   marginTop: "auto",
   paddingBottom: theme.spacing(1),
   width: "100%"
}));

const FormWrapper = styled(Box)(() => ({
   flex: 1,
   minHeight: 0,
   padding: "0 3px",
   display: "flex",
   flexDirection: "column",
   overflowX: "hidden",
   overflowY: "auto"
}));

const onErrorDS = [
   { value: "stop", label: "Abort Flow Execution" },
   { value: "continue", label: "Continue Next" }
];

const FlowForm = ({ allowEditWorkflow, children }) => {
   const { user } = useAuth0();
   const { flow, currentSavedFlow, savingFlowPropertiesFlagSuccess, flows, copiedNode, canvasFlowInstance } = useSelector((store) => store?.designer?.flows);

   const { flowTriggers } = useSelector((store) => store?.designer?.triggers);
   const { context } = useSelector((store) => store?.designer?.editorContext);
   const { tenantData } = useSelector((store) => store?.designer?.tenant);
   const { t } = useTranslation("flowDesigner");
   const [submitting, setSubmitting] = useState(false);
   const formRef = useRef(null);
   const dispatch = useDispatch();
   const { integrations } = useIntegrations();
   const flow_settings = isValidJsonString(currentSavedFlow?.flow_settings) ? JSON.parse(currentSavedFlow.flow_settings) : currentSavedFlow.flow_settings ? currentSavedFlow.flow_settings : {};
   initialValues.flowName = currentSavedFlow?.name;
   initialValues.description = flow?.description || currentSavedFlow?.description;
   initialValues.executionTimeoutDuration = flow_settings?.execution_timeout_duration?.toString() || getEnv("EXECUTION_TIMEOUT_DURATION").toString();
   initialValues.retryOnFail = typeof flow_settings?.retry_on_fail === "boolean" ? flow_settings?.retry_on_fail : false;
   initialValues.maximumTries = flow_settings?.maximum_tries || 3;
   initialValues.waitBetweenTries = flow_settings?.wait_between_tries || 1000;
   initialValues.onError = flow_settings?.on_error || "stop";
   initialValues.notes = flow_settings?.notes || "";
   initialValues.integration = currentSavedFlow?.integrations ? integrations?.filter((integration) => currentSavedFlow.integrations.includes(integration.id)).map((integration) => integration.id) : [];
   initialValues.displayExecutionLogs =
      currentSavedFlow?.shared_status === "active" ? (flow_settings?.display_execution_logs?.active ?? false) : (flow_settings?.display_execution_logs?.draft ?? true);
   initialValues.returnValue = flow_settings?.return_value ?? initialValues.returnValue;

   const { formId, formField: { flowName } } = flowFields;

   const handleOpenDiscardDialog = () => {
      dispatch(openDiscardDialog());
   };

   const checkDirtyAndClose = () => {
      if (formRef.current.dirty) {
         handleOpenDiscardDialog();
         return;
      }
      closeForm();
   };

   const closeForm = () => {
      resetForm();

      dispatch(closeToolboxDetails());
      dispatch(clearToolboxSelectedMenuItem());
   };

   const resetForm = () => {
      formRef.current?.resetForm();
   };

   const getFlowSteps = () => {
      let steps = canvasFlowInstance;
      //clear the base64 urls
      steps?.nodes.forEach((s) => {
         if (s.data.logo && s.data?.logo.indexOf("http") === -1) {
            s.data.logo = "";
         }
      });

      const { adjustedTriggers, adjustedContext } = adjustTriggerCredentials(flowTriggers, context);

      steps.context = adjustedContext;
      steps.triggers = adjustedTriggers;

      steps.edges = steps.edges.filter((edge) => edge.type !== EDGE_TYPES.FLOATING);

      // store the copied node in the saved flow to be able to paste it later even if the browser reloads
      if (copiedNode) {
         steps.copiedNode = copiedNode;
      }

      steps = JSON.stringify(steps);

      return steps;
   };

   const createFlowIntegrationValues = async (formValues) => {
      const flowIntegrations = [];

      const newIntegrations = Array.isArray(formValues?.integration) ? formValues.integration.filter((integration) => !integrations.find((portalIntegration) => portalIntegration.id === integration)) : [];

      const existingIntegrations = Array.isArray(formValues?.integration) ? formValues.integration.filter((integration) => integrations.find((portalIntegration) => portalIntegration.id === integration)) : [];

      existingIntegrations.forEach((integration) => flowIntegrations.push(integration));

      const handleCreateNewIntegrations = async () => {
         const results = await Promise.all(
            newIntegrations.map(async (integration) => {
               const input = {
                  tenant_id: user.stripeId,
                  integration_name: integration,
                  flow_ids: [],
                  description: "",
                  create_user_id: user.cxfUserId,
                  modified_user_id: user.cxfUserId,
                  current_version: "1",
                  status: "active"
               };
               return Object.assign(await insertIntegrations(input), input);
            })
         );

         results.forEach((result) => {
            flowIntegrations.push(result[0].id);
            dispatch(addIntegration(result[0]));
         });
      };

      await tryCatchWrapper(handleCreateNewIntegrations)();

      return flowIntegrations;
   };

   const handleSaveSubmit = async (values, actions) => {
      try {
         const tenant_id = user?.stripeId;
         const steps = getFlowSteps(values.flowName);
         const getFieldOrEmpty = (fieldName) => {
            return currentSavedFlow ? currentSavedFlow[fieldName] : "";
         };

         setSubmitting(true);
         actions.setSubmitting(false);

         const integrations = await createFlowIntegrationValues(values);
         const parsedSteps = JSON.parse(steps);
         parsedSteps.context.integration_ids = integrations ?? [];

         const input = {
            created_by: currentSavedFlow ? currentSavedFlow.created_by : user?.name,
            created_by_id: currentSavedFlow ? currentSavedFlow.created_by_id : user?.cxfUserId,
            description: values.description || "",
            group_id: getFieldOrEmpty("group_id"),
            group_name: getFieldOrEmpty("group_name"),
            name: values.flowName,
            notes: "",
            platform_name: getFieldOrEmpty("platform_name"),
            platform_type: getFieldOrEmpty("platform_type"),
            shared_link: getFieldOrEmpty("shared_link"),
            shared_status: currentSavedFlow?.shared_status ?? "active",
            steps: JSON.stringify(parsedSteps),
            trashed: currentSavedFlow ? currentSavedFlow.trashed : false,
            flow_settings: JSON.stringify({
               execution_timeout_duration: parseInt(values.executionTimeoutDuration),
               retry_on_fail: values.retryOnFail,
               maximum_tries: values.maximumTries,
               wait_between_tries: values.waitBetweenTries,
               on_error: values.onError,
               notes: values.notes,
               return_value: values.returnValue
            }),
            integrations: JSON.stringify(integrations)
         };

         const sharedStatusKey = currentSavedFlow?.shared_status === "active" ? "active" : "draft";
         const currentSavedFlowSettings = isValidJsonString(currentSavedFlow?.flow_settings) ? JSON.parse(currentSavedFlow.flow_settings) : currentSavedFlow?.flow_settings ?? {};

         input.flow_settings = JSON.stringify({
            ...currentSavedFlowSettings,
            ...JSON.parse(input.flow_settings),
            display_execution_logs: {
               ...currentSavedFlowSettings?.display_execution_logs,
               [sharedStatusKey]: values.displayExecutionLogs
            }
         });

         if (currentSavedFlow?.id) {
            dispatch(updateCurrentFlowProperties(tenant_id, currentSavedFlow.id, input, user.cxfUserId));
         } else {
            dispatch(createNewFlowProperties(tenant_id, input));
         }

         dispatch(setContext(parsedSteps.context));
         actions.resetForm({ values });
      } catch (error) {
         console.log(error);
         toast.error("Failed to save the flow");
      }

      setSubmitting(false);
   };

   const handleSubmit = (event) => {
      event.preventDefault();
      formRef.current.submitForm();
   };

   const createIntegrationsOptions = () => {
      const mappedIntegrations = integrations.map((integration) => ({
         value: integration.id,
         label: integration.integration_name
      }));

      return mappedIntegrations.sort((first, second) => (first.label || "").localeCompare(second.label || ""));
   };

   // validate execution timeout field if it is greater than the tenant execution timeout duration or the global execution timeout duration
   const validateExecutionTimeoutDuration = (value) => {
      let error;
      let tenantExecutionTimeoutDuration;
      let defaultExecutionTimeoutDuration = getEnv("EXECUTION_TIMEOUT_DURATION");

      try {
         const parsedTenantDataConfig = JSON.parse(tenantData.config);
         if (parsedTenantDataConfig.execution_timeout_duration) {
            tenantExecutionTimeoutDuration = parsedTenantDataConfig.execution_timeout_duration;
         }
      } catch (error) {
         defaultExecutionTimeoutDuration = getEnv("EXECUTION_TIMEOUT_DURATION");
      }

      if (!tenantExecutionTimeoutDuration && value && parseInt(value) > defaultExecutionTimeoutDuration) {
         error = `Execution timeout must be less than ${defaultExecutionTimeoutDuration} seconds`;
      }

      if (tenantExecutionTimeoutDuration && value && (parseInt(value) > tenantExecutionTimeoutDuration || parseInt(value) < defaultExecutionTimeoutDuration)) {
         error = `Execution timeout must be between ${defaultExecutionTimeoutDuration} seconds and ${tenantExecutionTimeoutDuration} seconds`;
      }

      return error;
   };

   const handleExportFlowToJson = () => {
      try {
         const steps = JSON.parse(currentSavedFlow?.steps);
         const nodes = steps.nodes;
         const sanitizedNodes = nodes.map((node) => {
            if (node.type !== NODE_TYPES.STATIC) {
               const nodeFlowElement = node.data?.flowElement;

               if (!nodeFlowElement?.isCredentialSupported) return node;

               nodeFlowElement.data["credential"] = {
                  value: null
               };

               if (!nodeFlowElement.data[CREDENTIAL_REQUIRED]) {
                  nodeFlowElement.data[CREDENTIAL_REQUIRED] = {
                     value: false
                  };
                  nodeFlowElement.data[CREDENTIAL_TYPE] = {
                     value: null
                  };
               }

               // so that when new users of the exported flow will know what type of credential the old flow used on a certain node
               if (nodeFlowElement?.data[CREDENTIAL_TYPE]?.value) {
                  nodeFlowElement.data[ORIGINAL_CREDENTIAL_TYPE] = {
                     value: nodeFlowElement.data[CREDENTIAL_TYPE].value
                  };
               }

               return {
                  ...node,
                  data: {
                     ...node.data,
                     flowElement: nodeFlowElement
                  }
               };
            }

            return node;
         });

         exportToJson({
            ...currentSavedFlow,
            steps: JSON.stringify({
               ...steps,
               nodes: sanitizedNodes
            })
         });
      } catch (error) {
         console.error(error);
      }
   };

   const validateFlowName = (value) => {
      let error;

      if (!value) {
         error = `${flowName.label} is required.`;
         return error;
      }

      if (flows && flows.length > 0) {
         const flowExists = flows.some((flow) => flow.id !== currentSavedFlow.id && flow.name.toLowerCase() === value.toLowerCase());
         if (flowExists) {
            error = `${value} already exists.`;
            return error;
         }
      }
   };

   const createIntegrationDefaultValue = (formIntegrations = []) => {
      const filteredIntegrations = integrations
         ?.filter((integration) => formIntegrations.includes(integration.id))
         ?.map((integration) => ({
            value: integration.id,
            label: integration.integration_name
         }));

      return filteredIntegrations ?? [];
   };

   useEffect(() => {
      if (savingFlowPropertiesFlagSuccess && currentSavedFlow) {
         dispatch(setSavingFlowPropertiesFlagReset());
      }
   }, [savingFlowPropertiesFlagSuccess]);

   return (
      <Box sx={{ height: "calc(100% - 48px)", display: "flex", flexDirection: "column" }}>
         <FormWrapper>
            <Formik initialValues={initialValues} onSubmit={handleSaveSubmit} validationSchema={validations} innerRef={formRef} enableReinitialize>
               <FlowFormContext.Provider
                  value={{
                     allowEditWorkflow,
                     currentSavedFlow,
                     createIntegrationDefaultValue,
                     createIntegrationsOptions,
                     fields: flowFields.formField,
                     onErrorOptions: onErrorDS,
                     validateExecutionTimeoutDuration,
                     validateFlowName
                  }}
               >
                  <Form id={formId} autoComplete="off">
                     {children}
                  </Form>
               </FlowFormContext.Provider>
            </Formik>
            <DiscardChangesDialog onAccept={closeForm} />
         </FormWrapper>
         <ButtonsWrapper>
            <SoftBox
               className="btns-action"
               sx={{
                  display: "flex",
                  gap: 2,
                  padding: "5px",
                  justifyContent: "flex-end",
                  width: "100%"
               }}
            >
               <SoftButton sx={{ minWidth: "110px", m: 0 }} variant="outlined" color="info" onClick={handleExportFlowToJson}>
                  {"Export"}
               </SoftButton>
               <SoftButton sx={{ minWidth: "110px", m: 0 }} variant="outlined" color="info" onClick={checkDirtyAndClose}>
                  {t("buttons.close", "Close")}
               </SoftButton>
               <SoftButton sx={{ minWidth: "110px", m: 0 }} color="primary" variant="contained" disabled={submitting || !allowEditWorkflow} onClick={handleSubmit}>
                  {t("buttons.save")}
               </SoftButton>
            </SoftBox>
         </ButtonsWrapper>
      </Box>
   );
};

export default memo(FlowForm);
